import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/site";
import { getMovieById } from "@/lib/media";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const goTo = (next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStart.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 48) goTo(index + (delta < 0 ? 1 : -1));
    touchStart.current = null;
  };

  const slide = heroSlides[index];
  const movie = getMovieById(slide.movieId);

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="热门影视推荐轮播"
    >
      {heroSlides.map((item, itemIndex) => (
        <div
          key={item.movieId}
          className={`absolute inset-0 transition-opacity duration-700 ${
            itemIndex === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden="true"
        >
          <img
            src={item.backdrop}
            alt={`${item.title}背景海报`}
            title={`${item.title} 热门影视推荐 色虎`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-900/40" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/30" aria-hidden="true" />

      <div className="portal-container relative flex min-h-[520px] flex-col justify-center py-12 md:min-h-[560px] lg:min-h-[600px]">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_300px]">
          <div key={slide.movieId} className="hero-slide-active w-full">
            <p className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-accent-300">
              <span className="h-[2px] w-6 bg-accent-400" aria-hidden="true" />
              色虎 · 影视娱乐门户
            </p>
            <h1 className="font-heading text-2xl font-bold tracking-wide text-background-50 md:text-3xl">
              色虎
              <span className="ml-3 text-sm font-normal tracking-normal text-primary-200 md:text-base">
                热门影视剧推荐与资讯平台
              </span>
            </h1>
            <h2 className="mt-5 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              {slide.title}
            </h2>
            <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-primary-200">
              <span className="rounded bg-accent-500 px-2 py-0.5 font-heading text-sm font-bold text-primary-950">
                {slide.rating.toFixed(1)} 分
              </span>
              <span>{slide.subtitle}</span>
              <span className="text-primary-400">|</span>
              <span>{slide.kicker}</span>
            </p>
            <p className="mt-4 max-w-xl text-sm leading-7 text-primary-100 md:text-base">{slide.description}</p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.movieId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-circle-line text-base" aria-hidden="true" />
                查看影片详情
              </Link>
              <Link
                to="/category/movie"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-400/60 bg-primary-900/40 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                浏览分类
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {movie && (
            <Link
              to={`/detail/${movie.id}`}
              className="group hidden overflow-hidden rounded-lg border border-primary-400/40 bg-primary-900/50 lg:block"
              aria-label={`查看 ${movie.title} 详情`}
            >
              <div className="aspect-[2/3] w-full overflow-hidden">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title} 影视详情 色虎`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="truncate text-xs text-background-100">{movie.genres.join(" / ")}</span>
                <span className="text-xs font-medium text-accent-300">{movie.duration}</span>
              </div>
            </Link>
          )}
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {heroSlides.map((item, itemIndex) => (
              <button
                key={item.movieId}
                type="button"
                onClick={() => goTo(itemIndex)}
                aria-label={`切换到${item.title}`}
                aria-current={itemIndex === index}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  itemIndex === index ? "w-8 bg-accent-500" : "w-4 bg-primary-300/60 hover:bg-primary-200"
                }`}
              />
            ))}
            <span className="ml-2 font-heading text-xs text-primary-300">
              0{index + 1} / 0{heroSlides.length}
            </span>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="上一张"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-primary-400/60 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
            >
              <i className="ri-arrow-left-line" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="下一张"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-primary-400/60 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
            >
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}